import { Box, Collapse, Flex, Grid, Input, Text, Tooltip, useOutsideClick } from '@chakra-ui/react'
import type { OfferPackage, OfferProperty, OfferTravel, PackageItemModel, PartPackage } from 'BrokerTsLib/src/dtos'
import { Insurer, Risk } from 'BrokerTsLib/src/enums'
import { format, parse } from 'BrokerTsLib/src/Utils/formatInputs'
import { priceFormat } from 'BrokerTsLib/src/Utils/formatPrice'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useApp } from '../../../../hooks/useApp'
import { DismissCircle } from '../../../../icons'
import { GetPackagePartRenderType } from '../components/OfferCell'
import { TinySelect } from '../components/TinySelect'
import Bicycle from './property/Bicycle'
import ConstructionAssembly from './property/ConstructionAssembly'
import Medical from './property/Medical'
import MusicalInstruments from './property/MusicalInstruments'
import Pet from './property/Pet'
import PetsTravel from './travel/Pets'
import SportEquipments from './travel/SportEquipments'
import Vehicles from './travel/Vehicles'

export const AdditionValueEditor = ({
  offer, addon, part, isOpen, onChange
}: {
  offer: OfferPackage | OfferProperty | OfferTravel
  addon: PackageItemModel
  part?: PartPackage // nemuzu brat primo z offery, protoze uz muze byt parta editovana. Tak si ji sem radeji predavam
  isOpen: boolean
  onChange: (limit: number, variantId?: number) => void
}) => {
  const { app } = useApp()

  const isChaloupka = app == 'chaloupka'
  const isCestovko = app == 'cestovko'

  const specificEditor = useMemo(() => {
    if (isChaloupka) {
      const offerProperty = offer as OfferProperty

      if (offer.insurerId == Insurer.Direct && addon.riskId == Risk.ADDITION_PET)
        return <Pet offer={offerProperty} addon={addon} />

      if (offer.insurerId == Insurer.Direct && addon.riskId == Risk.ADDITION_BICYCLE)
        return <Bicycle offer={offerProperty} addon={addon} />

      if ([Insurer.Direct, Insurer.GeneraliCeskaPojistovna].includes(offer.insurerId) && addon.riskId == Risk.ADDITION_MEDICAL)
        return <Medical offer={offerProperty} addon={addon} />

      if ([Insurer.GeneraliCeskaPojistovna].includes(offer.insurerId) && addon.riskId == Risk.ADDITION_MUSICAL_INSTR)
        return <MusicalInstruments offer={offerProperty} addon={addon} />

      if ([Insurer.GeneraliCeskaPojistovna].includes(offer.insurerId) && addon.riskId == Risk.ADDITION_CONSTRUCTION_ASSEMBLY)
        return <ConstructionAssembly offer={offerProperty} addon={addon} />
    } else if (isCestovko) {
      const offerTravel = offer as OfferTravel

      if (offer.insurerId == Insurer.Direct && addon.riskId == Risk.ADDITION_TRAVEL_SPORT_EQUIPMENT)
        return <SportEquipments offer={offerTravel} addon={addon} />

      if (offer.insurerId == Insurer.Direct && addon.riskId == Risk.ADDITION_MINI_KASKO)
        return <Vehicles offer={offerTravel} addon={addon} />

      if (offer.insurerId == Insurer.Direct && addon.riskId == Risk.ADDITION_TRAVEL_PET)
        return <PetsTravel offer={offerTravel} addon={addon} />
    }

  }, [])

  let children = specificEditor
  if (!specificEditor) {
    const childrenType = GetPackagePartRenderType(addon, part)

    const [value, packageItemVariantId] = part?.limit
      ? [part?.limit, part?.packageItemVariantId]
      : (addon.variants && addon.variants.length > 0
        ? [addon.variants[0].defaultLimit!, addon.variants[0].id]
        : [])

    const valueText = value == 999999999 ? 'Neomezeně' : value ? priceFormat(value) : undefined

    switch (childrenType) {
      case 'limit':
        {
          const variant = addon.variants[0]
          children = (
            <CustomPriceEditor
              value={value}
              min={variant.minLimit}
              max={variant.maxLimit}
              onSubmit={(limit) => {
                onChange(limit, variant.id)
              }} />
          )
          break
        }
      case 'options':
        children = <VariantSelector
          limit={part?.limit}
          packageItem={addon}
          packageItemVariantId={packageItemVariantId}
          onSubmit={(limit, variantId) => {
            onChange(limit, variantId)
          }} />
        break
      case 'text':
      case 'bool':
      default:
        children = valueText
          ? <Text sx={{ p: 0, fontSize: 'sm', textDecoration: 'underline', textDecorationStyle: 'dotted', mr: 5 }}>
            {valueText}
          </Text>
          : <DismissCircle color="red.500" />
        break
    }
  }

  return (
    <>
      <Grid
        gridColumn={specificEditor && addon.riskId !== Risk.ADDITION_MINI_KASKO ? 'span 2' : undefined} // specific editory zobrazujeme na celou sirku
      >
        <Collapse in={isOpen}>
          <Box my={4}>
            {children}
          </Box>
        </Collapse>
      </Grid>
    </>
  )
}

type VariantSelectorProps = {
  packageItem: PackageItemModel
  packageItemVariantId?: number
  limit?: number
  onSubmit: (limit: number, variantId?: number) => void
}

export const VariantSelector = ({ packageItem, limit, onSubmit }: VariantSelectorProps) => {
  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <TinySelect
        onChange={(e) => {
          const dataset = e.target.options[e.target.selectedIndex].dataset
          onSubmit(parseInt(e.target.value), parseInt(dataset.id!))
        }}
        value={limit?.toString() ?? ''}
      >
        <option disabled value=''>Vyberte</option>
        {packageItem.variants.map((option) => {
          if (!option.defaultLimit || !option.id) return null

          return (
            <option key={option.defaultLimit} value={option.defaultLimit.toString()} data-id={option.id}>
              {option.defaultLimit == 999999999 ? 'Neomezeně' : priceFormat(option.defaultLimit)}
            </option>

          )
        })}
      </TinySelect>
    </Box>
  )
}


type CustomPriceEditorProps = {
  value?: number | string
  onSubmit: (limit: number) => void
  min?: number
  max?: number
  unit?: string
  errorMessage?: string
}


export const CustomPriceEditor = ({ value, onSubmit, min, max, unit, errorMessage }: CustomPriceEditorProps) => {
  const [inputVal, setInputVal] = useState(format(value?.toString() || '') || '')

  const [error, setError] = useState(errorMessage)

  useEffect(() => {
    setError(error)
  }, [errorMessage])

  const formatValue = (value?: number) => {
    if (unit && unit == '%')
      return `${value}${unit ?? ''}`

    return priceFormat(value)
  }

  const submit = useCallback(() => {
    const num = inputVal ? parseInt(inputVal) : 0
    if (min && num < min) {
      setError('Minimální možná hodnota je ' + formatValue(min))
      return
    }

    if (max && num > max) {
      setError('Maximální možná hodnota je ' + formatValue(max))
      return
    }

    onSubmit(num)
  }, [inputVal])

  const ref = useRef<HTMLInputElement>(null)
  useOutsideClick({
    ref: ref,
    handler: () => submit()
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const parsed = parse(value)

    setInputVal(parsed)
  }

  const textSx = { p: 0, fontSize: 'xs' }

  return (

    <form onSubmit={(e) => {
      e.preventDefault()
      submit()
    }}>
      <Box sx={{ display: 'flex', gap: 1 }}>

        <Tooltip label={error}>
          <Flex gap={2}>
            {(!!min || min === 0) && (!!max || max === 0) &&
              <Text sx={{ ...textSx }}>
                {formatValue(min)} - {formatValue(max)}
              </Text>
            }

            <Input ref={ref}
              type='text'
              isInvalid={!!error}
              value={inputVal}
              onChange={handleChange}
              sx={{
                w: 'auto',
                maxW: '100px',
                py: 0.25,
                bg: 'white',
                fontSize: 'sm',
                height: 'auto',
                lineHeight: '19px',
                textAlign: 'end'
              }}
            />
          </Flex>
        </Tooltip>

      </Box>
    </form>

  )
}